import { ApiError } from '../../api';

export const INSUFFICIENT_BALANCE_MESSAGE = 'Insufficient balance. Please add funds and try again.';
export const SPENDING_LIMIT_MESSAGE = "You've reached your spending limit.";

/** Backend error code of a failed purchase; undefined for transport / 5xx. */
export function purchaseErrorCode(err: unknown): string | undefined {
  return err instanceof ApiError ? err.code : undefined;
}

/** The operator's wallet refused the debit over the player's spending limit
 *  (SPENDING_LIMIT_REACHED, SPENDING_LIMIT_REACHED_DAY, …). The backend's
 *  message is already player-facing; topping up or retrying won't help. */
export function isSpendingLimitCode(code: string | undefined): boolean {
  return code === 'SPENDING_LIMIT_REACHED' || (code?.startsWith('SPENDING_LIMIT_REACHED_') ?? false);
}

/**
 * Maps an instant_purchase failure to user-facing copy for the failure
 * screen. instant_purchase is strictly atomic — ANY failure refunds — so
 * every case is safe for the user to simply try again.
 *
 *   • 409 INSTANT_PURCHASE_IN_PROGRESS — a parallel duplicate (same idempotency
 *     key still settling). Soft copy: just try again in a moment.
 *   • INSUFFICIENT_BALANCE — the client-side pre-flight check in
 *     useGamePurchase/WinnerStep should catch this first, but a stale local
 *     balance (e.g. another tab spent it) can still slip through — surface
 *     the real reason instead of the generic copy below.
 *   • everything else (422 / 5xx / network) — generic "didn't go through,
 *     nothing was charged, try again" so the user never fears a lost charge.
 */
export function purchaseErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'INSTANT_PURCHASE_IN_PROGRESS' || err.status === 409) {
      return "That pack is still being prepared. Give it a moment and try again.";
    }
    if (err.code === 'INSUFFICIENT_BALANCE') {
      return INSUFFICIENT_BALANCE_MESSAGE;
    }
    if (isSpendingLimitCode(err.code)) {
      return err.message || SPENDING_LIMIT_MESSAGE;
    }
    // Provably fair: the rarity tier the seeds picked emptied in the meantime.
    // Never re-rolled into another tier — the purchase is refunded instead.
    // Provably fair is on but no ticket could be had: the purchase was never sent.
    if (err.code === 'FAIRNESS_UNAVAILABLE') {
      return err.message;
    }
    if (err.code === 'TIER_EXHAUSTED') {
      return "The rarity tier you rolled just sold out, so you weren't charged. Please try again.";
    }
  }
  // Backend 422/5xx already refunded; surface a reassuring, retry-friendly note.
  return "That didn't go through and you weren't charged. Please try again.";
}
